import { ApplicationTypeKey } from "@hub/data/values/data-keys.enum";
import { HeadMetadata } from "@hub/models/metadata/head-metadata";
import { BannerConfig } from "@hub/models/ui/banner-config";
import { Breadcrumb } from "@hub/models/ui/breadcrumb";
import { Card } from "@hub/models/ui/card";
import { ContentImage } from "@hub/models/ui/content-image";
import { PageConfig } from "@hub/models/ui/page-config";
import { hubOriginUrl, hubSiteTree } from "@hub/services/config.service";
import { getBreadCrumbs } from "@hub/services/content/common/get-breadcrumbs";
import { GetStartedData } from "@hub/services/content/models/get-started-data";
import { MetadataService } from "@hub/services/metadata.service";
import { createUrlPath } from "@hub/services/utils/path.utils";
import { GetStartedPage } from "@hub/ui/pages/get-started-page";
import { GetStaticProps } from "next";
import React from "react";

interface GetStartedIndexProps {
  canonicalUrl: string;
  breadcrumbs?: Breadcrumb[];
  cardData: Card[];
}

const GetStartedIndex: React.FC<GetStartedIndexProps> = ({
  canonicalUrl,
  breadcrumbs,
  cardData,
}) => {
  const headMetadata: HeadMetadata = {
    title: "Get Started with Auth0",
    description:
      "Discover how the Auth0 Identity Platform integrates with your applications.",
    url: canonicalUrl,
    image:
      "https://cdn.auth0.com/website/developer-center/preview-cards/resources_get-started.png",
    type: "website",
  };

  const pageConfig: PageConfig = {
    canonicalUrl,
    title: "Auth0 Authentication and Authorization Code Samples",
    description:
      "Use different frontend and backend frameworks and languages to explore the authentication and authorization features of the Auth0 Identity Platform.",
  };

  const bannerConfig: BannerConfig = {
    title: "Get Started with Auth0",
    description:
      "Discover how the Auth0 Identity Platform integrates with your applications.",
    withBorder: true,
  };

  return (
    <GetStartedPage
      bannerConfig={bannerConfig}
      pageConfig={pageConfig}
      breadcrumbs={breadcrumbs}
      headMetadata={headMetadata}
      cardData={cardData}
    />
  );
};

export default GetStartedIndex;

export const getStaticProps: GetStaticProps<
  GetStartedIndexProps
> = async () => {
  const getStartedData = new GetStartedData();

  const getStartedCardsByAppType: Card[] = [];

  const allowedApplicationTypes = getStartedData.allowed.applicationTypes;
  const allowedApplicationTypesInOrder: ApplicationTypeKey[] = [];

  const spaIndex = allowedApplicationTypes.indexOf(ApplicationTypeKey.SPA);

  if (spaIndex > -1) {
    allowedApplicationTypesInOrder.push(ApplicationTypeKey.SPA);
  }

  const apiIndex = allowedApplicationTypes.indexOf(ApplicationTypeKey.API);

  if (apiIndex > -1) {
    allowedApplicationTypesInOrder.push(ApplicationTypeKey.API);
  }

  const webAppIndex = allowedApplicationTypes.indexOf(
    ApplicationTypeKey.WEB_APP,
  );

  if (webAppIndex > -1) {
    allowedApplicationTypesInOrder.push(ApplicationTypeKey.WEB_APP);
  }

  const mobileIndex = allowedApplicationTypes.indexOf(
    ApplicationTypeKey.MOBILE,
  );

  if (mobileIndex > -1) {
    allowedApplicationTypesInOrder.push(ApplicationTypeKey.MOBILE);
  }

  const cloudIndex = allowedApplicationTypes.indexOf(ApplicationTypeKey.CLOUD);

  if (cloudIndex > -1) {
    allowedApplicationTypesInOrder.push(ApplicationTypeKey.CLOUD);
  }

  const mlhIndex = allowedApplicationTypes.indexOf(ApplicationTypeKey.MLH);

  if (mlhIndex > -1) {
    allowedApplicationTypesInOrder.push(ApplicationTypeKey.MLH);
  }

  for (const appType of allowedApplicationTypesInOrder) {
    const applicationTypeDisplayName =
      MetadataService.getApplicationTypeDisplayName(appType);

    if (!applicationTypeDisplayName) {
      throw new Error(
        "Unable to get application type display name for Get Started Page.",
      );
    }

    const applicationTypeIcon = MetadataService.getApplicationTypeIcon(appType);

    if (!applicationTypeIcon) {
      throw new Error(
        "Unable to get application type icon for Get Started Page.",
      );
    }

    let description = "";
    let illustration: ContentImage | null = null;

    switch (appType) {
      case ApplicationTypeKey.API:
        description = "An API or service protected by Auth0.";
        illustration = {
          src: "/resources/logos/api-logo.svg",
          alt: "API logo",
          height: 120,
          width: 120,
        };

        break;
      case ApplicationTypeKey.SPA:
        description = "JavaScript web app that runs in the browser.";
        illustration = {
          src: "/resources/logos/spa-logo.svg",
          alt: "SPA logo",
          height: 120,
          width: 120,
        };

        break;
      case ApplicationTypeKey.WEB_APP:
        description = "Traditional web app that runs on the server.";
        illustration = {
          src: "/resources/logos/web-app-logo.svg",
          alt: "Web app logo",
          height: 120,
          width: 120,
        };

        break;
      case ApplicationTypeKey.MOBILE:
        description = "Mobile app that runs natively on a device.";
        illustration = {
          src: "/resources/logos/mobile-logo.svg",
          alt: "Mobile app logo",
          height: 120,
          width: 120,
        };

        break;
      case ApplicationTypeKey.CLOUD:
        description = "Secure web applications hosted in the cloud.";
        illustration = {
          src: "/resources/logos/cloud-logo.svg",
          alt: "Mobile app logo",
          height: 120,
          width: 120,
        };

        break;
      case ApplicationTypeKey.MLH:
        description = "Move fast without breaking things!";
        illustration = {
          src: "/resources/logos/zap-logo.svg",
          alt: "Zap logo",
          height: 120,
          width: 120,
        };

        break;
    }

    if (!illustration) {
      throw new Error(
        "Unable to get application type icon for Get Started Page.",
      );
    }

    getStartedCardsByAppType.push({
      title: applicationTypeDisplayName,
      description: description,
      link: `/resources/get-started/${appType}`,
      illustration,
      // illustration: applicationTypeIcon as string,
    });
  }

  const breadcrumbs = getBreadCrumbs({
    root: {
      label: hubSiteTree.root.displayName,
      path: hubSiteTree.root.urlPath,
    },
    sections: [
      {
        label: hubSiteTree.root.sections.getStarted.displayName,
        path: hubSiteTree.root.sections.getStarted.urlPath,
      },
    ],
  });

  const canonicalUrl =
    hubOriginUrl +
    createUrlPath([
      hubSiteTree.root.urlPath,
      hubSiteTree.root.sections.getStarted.urlPath,
    ]);

  const props: GetStartedIndexProps = {
    canonicalUrl,
    breadcrumbs,
    cardData: getStartedCardsByAppType,
  };

  return {
    props,
  };
};
